import { useAppDispatch, useAppSelector } from 'hooks/useAppDispatchSelector';
import { useContext, useState, useRef, useEffect } from 'react';
import { AppContext } from 'context/app-context';
import { setModal } from 'store/features/modelSlice';
import { initModal } from 'helpers/utils';
import PrimaryDarkButton from 'components/common/buttons/primary-dark-btn';
import DarkButton from 'components/common/buttons/dark-btn';
import InputField from 'components/common/input-field';
import { ReactComponent as CrossIcon } from 'assets/icon/x-circle.svg';
import { createModel, getBrandList, updateModel } from 'store/features/vehicleConfigSlice';
import toast from 'react-hot-toast';
import PrimaryButtom from 'components/common/buttons/primary-btn';
import { IUploadImageRequestParam } from 'store/features/organizationSlice';
import { getVehicleUploadImageLocation } from 'store/features/vehicleSlice';
import Select, { IDefaultOption } from 'components/common/select';
import { IModel } from 'interfaces/vehicle-config-interfaces';
import { useTranslation } from 'react-i18next';

const initModelData: IModel = { name: '', brandDetails: null, image_url: '' };
const AddEditModel = () => {
  const dispatch = useAppDispatch();
  const {
    modal: {
      content: { title, formFor, callBackFun },
    },
  } = useAppSelector(state => state.modal);
  const { brands, selectedModel } = useAppSelector(state => state.vehicleConfig);
  const { setLoader } = useContext(AppContext);
  const [pagination, setPagination] = useState({
    limit: 0,
    offset: 0
  })
  const [modelData, setModelData] = useState<IModel>(
    formFor === 'EDIT' && selectedModel ? selectedModel : initModelData,
  );
  const [brandOptions, setBrandOptions] = useState<IDefaultOption[]>([]);
  const [error, setError] = useState<{ model: string; brand: string } | null>();
  const [brandImg, setBrandImg] = useState<{
    file?: null | File;
    url?: string;
    key?: string;
  } | null>(
    formFor === 'EDIT' && selectedModel
      ? { url: selectedModel?.image_url, key: selectedModel?.assetDetails?.url }
      : null,
  );
  let logoRef = useRef<HTMLInputElement | null>(null);
  const { t } = useTranslation();

  const uploadLogo = async () => {
    try {
      if (brandImg?.file) {
        setLoader(true);
        const params: IUploadImageRequestParam = {
          request: {
            file_name: brandImg?.file?.name || '',
            file_size: brandImg?.file?.size || 0,
            file_type: brandImg?.file?.type || '',
          },
          file: brandImg?.file,
        };
        const response: { key: string; download_url: string; url: string } = await dispatch(
          getVehicleUploadImageLocation({ params }),
        ).catch(() => {
          setLoader(false);
        });
        return Promise.resolve(response);
      }
    } catch (error) {
      setLoader(false);
    }
  };
  const handleAddModel = async () => {
    try {
      setLoader(true);
      const imgData = await uploadLogo();
      await dispatch(
        createModel({
          name: modelData?.name,
          image: imgData?.key || '',
          brand_id: modelData?.brandDetails?._id || '',
        }),
      )
        .then(() => {
          callBackFun && callBackFun();
          toast.success(t('workshopAppointment.successModelCreatedSuccessfully'));
          dispatch(setModal(initModal));
          setLoader(false);
        })
        .catch(() => {
          setLoader(false);
        });
      setLoader(false);
    } catch (error) {
      setLoader(false);
    }
  };
  const handleEditModel = async () => {
    try {
      setLoader(true);
      let imgURL = brandImg?.key;
      if (brandImg?.file) {
        const response = await uploadLogo();
        imgURL = response?.key;
      }
      await dispatch(
        updateModel({
          model_id: modelData?._id || '',
          request: {
            model_id: modelData?._id || '',
            name: modelData.name,
            image: imgURL || '',
            brand_id: modelData?.brandDetails?._id || '',
          },
        }),
      )
        .then(() => {
          callBackFun && callBackFun();
          toast.success(t('workshopAppointment.successModelUpdatedMessage'));
          dispatch(setModal(initModal));
          setLoader(false);
        })
        .catch(() => {
          setLoader(false);
        });
      setLoader(false);
    } catch (error) {
      setLoader(false);
    }
  };
  const handleSubmit = () => {
    try {
      if (!modelData?.brandDetails?._id || !modelData?.name?.trim()) {
        setError({
          ...error,
          brand: !modelData?.brandDetails?._id
            ? t('workshopAppointment.errorSelectBrandToastMessage')
            : '',
          model: !modelData?.name?.trim()
            ? t('workshopAppointment.errorModelNameToastMessage')
            : '',
        });
        return;
      }
      formFor === 'EDIT' ? handleEditModel() : handleAddModel();
    } catch (error) {
      setLoader(false);
    }
  };
  const setBrandImgData = (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      if (event?.target.files?.length) {
        setLoader(true);
        setBrandImg({
          file: event?.target?.files[0],
          url: window.URL.createObjectURL(event?.target?.files[0]),
        });
        setLoader(false);
      }
    } catch (error) {
      setLoader(false);
    }
  };
  const handleUploadLogo = () => {
    const fileElement = logoRef.current;
    fileElement?.click();
  };
  const fetchBrands = async () => {
    try {
      setLoader(true);
      await dispatch(getBrandList(pagination));
      setLoader(false);
    } catch (error) {
      setLoader(false);
    }
  };
  useEffect(() => {
    fetchBrands();
  }, []);
  useEffect(() => {
    const updatedBrands = brands.map(data => ({ id: data?._id || '', name: data?.name })) || [];
    setBrandOptions(updatedBrands);
  }, [brands]);
  const getSelectedBrandOption = () => {
    try {
      const selectedBrand: IDefaultOption | null =
        brandOptions.find(data => (data?.id || data?._id) === modelData?.brandDetails?._id) || null;
      if (selectedBrand) {
        return selectedBrand;
      } else {
        return modelData?.brandDetails
          ? { id: modelData?.brandDetails?._id || '', name: modelData?.brandDetails?.name || '' }
          : null;
      }
    } catch (error) {}
  };
  return (
    <div className="flex flex-wrap max-w-[500px] w-[500px] gap-y-4 overflow-hidden">
      <div className="flex items-center w-full">
        <div className="flex items-center flex-1">
          <label className="pl-2 text-xl font-semibold text-black2">{title}</label>
        </div>
        <div
          className="flex items-center justify-center cursor-pointer"
          onClick={() => dispatch(setModal(initModal))}
        >
          <CrossIcon height={32} width={32} />
        </div>
      </div>
      <div className="flex flex-wrap w-full max-h-[calc(100vh- 180px)] gap-y-1">
        <div className="flex items-center self-center w-full px-4 gap-x-2">
          <div className="w-20 h-20 flex items-center justify-center rounded-full bg-grey2">
            {!brandImg?.url ? (
              <label className="text-sm font-medium text-white">{t('NO_IMG')}</label>
            ) : (
              <img
                src={brandImg?.url}
                alt="Org-LOGO"
                className="h-full w-full rounded-full object-contain"
              />
            )}
          </div>
          <div className="flex items-center justify-center">
            <input
              type="file"
              className="hidden"
              ref={logoRef}
              onChange={event => setBrandImgData(event)}
              accept="image/*"
            />
            <PrimaryButtom
              label={
                !brandImg?.url ? t('organization.upload_image') : t('organization.change_image')
              }
              onClick={() => handleUploadLogo()}
              height="h-8"
              font_size="text-sm"
            />
          </div>
        </div>
        <div className="w-full">
          <label className="font-medium text-xs text-black2 w-full">{t('BRAND')}</label>
          <Select
            options={[...brandOptions]}
            setSelected={val =>
              setModelData({
                ...modelData,
                brandDetails: {
                  ...modelData?.brandDetails,
                  name: val?.name,
                  _id: val?._id || val?.id,
                },
              })
            }
            selected={getSelectedBrandOption() || null}
            placeholder={t('workshopAppointment.steps.brandPlaceholder')}
            error={error?.brand}
          />
        </div>
        <div className="w-full">
          <label className="font-medium text-xs text-black2 w-full">{t('MODEL')}</label>
          <InputField
            onChange={data => setModelData({ ...modelData, name: data })}
            error={error?.model}
            value={modelData.name}
            placeholder={t('workshopAppointment.steps.modelPLaceholder')}
          />
        </div>
      </div>
      <div className="flex items-center justify-center w-full gap-x-4 mt-6">
        <PrimaryDarkButton
          label={
            formFor === 'EDIT'
              ? t('workshopAppointment.saveChanges')
              : t('workshopAppointment.addModel')
          }
          onClick={() => handleSubmit()}
          width="w-1/2"
        />
        <DarkButton
          label={t('CANCEL')}
          width="w-1/2"
          onClick={() => dispatch(setModal(initModal))}
        />
      </div>
    </div>
  );
};

export default AddEditModel;
